import { getApiBaseUrl } from "./videoService.js";

export interface VariantInput {
  label: string;
  thumbnailUrl: string;
  customTitle?: string | null;
}

export interface CreateExperimentPayload {
  metric?: "WATCH_TIME" | "CTR";
  variants: VariantInput[];
}

export interface VariantAnalytics {
  id: string;
  experimentId: string;
  label: string;
  thumbnailUrl: string;
  customTitle?: string | null;
  impressions: number;
  clicks: number;
  ctr: number;
  watchTimeSec: number;
  watchTimeShare: number;
  isLeader: boolean;
}

export interface ExperimentAnalytics {
  id: string;
  videoId: string;
  metric: "WATCH_TIME" | "CTR";
  status: "RUNNING" | "COMPLETED" | "STOPPED";
  winningVariantId?: string | null;
  totalImpressions: number;
  totalClicks: number;
  totalWatchTimeSec: number;
  variants: VariantAnalytics[];
  leadingVariantId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Deterministic hash algorithm for consistent session-level variant distribution
 */
export function stringHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * Helper to get or generate persistent browser session ID
 */
export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "server_session";
  try {
    let sid = sessionStorage.getItem("hp_ab_session_id");
    if (!sid) {
      sid = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      sessionStorage.setItem("hp_ab_session_id", sid);
    }
    return sid;
  } catch {
    return "default_session";
  }
}

/**
 * Resolves an active experiment variant deterministically for the current session
 */
export function resolveExperimentVariant(
  video: any,
  sessionId?: string
): VariantAnalytics | null {
  const exp = video?.abExperiment || video?.experiment;
  const variants = exp?.variants;
  if (!variants || variants.length === 0) return null;

  if (exp.status && exp.status !== "RUNNING") {
    if (exp.winningVariantId) {
      return variants.find((v: any) => v.id === exp.winningVariantId) || variants[0];
    }
    return variants[0];
  }

  const sid = sessionId || getOrCreateSessionId();
  const vidId = video.id || video.uid || video.streamMediaId || "unknown";
  const seed = `${sid}:${vidId}`;
  const index = stringHash(seed) % variants.length;
  return variants[index] || variants[0];
}

/**
 * Fetch live experiment analytics for Creator Studio
 */
export async function fetchExperimentAnalytics(videoId: string): Promise<ExperimentAnalytics | null> {
  const baseUrl = getApiBaseUrl();
  try {
    const res = await fetch(`${baseUrl}/api/v1/studio/videos/${encodeURIComponent(videoId)}/experiments`);
    if (!res.ok) {
      if (res.status === 404) return null;
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    return data.experiment || null;
  } catch (err) {
    console.warn(`[experimentService] Error fetching experiment for video ${videoId}:`, err);
    return null;
  }
}

/**
 * Launch or restart a 2-3 variant A/B experiment
 */
export async function launchExperiment(
  videoId: string,
  payload: CreateExperimentPayload
): Promise<ExperimentAnalytics> {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/api/v1/studio/videos/${encodeURIComponent(videoId)}/experiments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to create experiment: HTTP ${res.status}`);
  }

  const data = await res.json();
  return data.experiment;
}

/**
 * Stop test and promote selected variant as permanent video thumbnail & title
 */
export async function selectWinningVariant(
  videoId: string,
  variantId: string
): Promise<{ success: boolean; experiment: ExperimentAnalytics }> {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/api/v1/studio/videos/${encodeURIComponent(videoId)}/experiments/select-winner`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ variantId }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to promote winning variant: HTTP ${res.status}`);
  }

  return await res.json();
}

/**
 * Manually stop an experiment
 */
export async function stopExperiment(
  videoId: string
): Promise<{ success: boolean; experiment: ExperimentAnalytics }> {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/api/v1/studio/videos/${encodeURIComponent(videoId)}/experiments/stop`, {
    method: "POST",
  });

  if (!res.ok) {
    throw new Error(`Failed to stop experiment: HTTP ${res.status}`);
  }

  return await res.json();
}

// In-memory set to prevent duplicate impression beacons in the same page view
const trackedImpressionsSet = new Set<string>();

/**
 * Record variant impression telemetry
 */
export async function recordImpressionTelemetry(variantId: string, videoId?: string): Promise<void> {
  const key = `${variantId}:${videoId || ""}`;
  if (trackedImpressionsSet.has(key)) return;
  trackedImpressionsSet.add(key);

  try {
    const baseUrl = getApiBaseUrl();
    await fetch(`${baseUrl}/api/v1/telemetry/experiment-impression`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variantId, videoId }),
      keepalive: true,
    });
  } catch {}
}

/**
 * Record variant click telemetry
 */
export async function recordClickTelemetry(variantId: string, videoId?: string): Promise<void> {
  try {
    const baseUrl = getApiBaseUrl();
    await fetch(`${baseUrl}/api/v1/telemetry/experiment-click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ variantId, videoId }),
      keepalive: true,
    });
  } catch {}
}
